// Synchronized superset rounds — the timeline logger's group idiom, lifted
// here so the ProgramComposer and the logger share ONE implementation
// (Antenore 2026-06-12: the composer's superset must behave like the logger's;
// two copies of these rules is a design failure, not a shortcut).
//
// A group renders interleaved: round 1 (1A, 1B, …), round 2 (2A, 2B, …).
// Adding appends one set to EVERY member — each seeded from its own last set,
// which is the caller's domain (seedNextSet in the logger, clone-last-row in
// the composer). Removing drops the whole round across members under the
// safety rules in removeRoundFromSets. Pure, UI-free.

export const memberLetter = (memberIdx: number): string =>
  String.fromCharCode(65 + memberIdx); // A, B, C, …

// "1A" in a multi-member table, plain "1" when there is a single member —
// the solo table is the same component, a group of one.
export const roundLabel = (
  roundIdx: number,
  memberIdx: number,
  memberCount: number,
): string =>
  memberCount > 1
    ? `${roundIdx + 1}${memberLetter(memberIdx)}`
    : `${roundIdx + 1}`;

export const groupMaxRounds = (counts: ReadonlyArray<number>): number =>
  counts.length === 0 ? 0 : Math.max(...counts);

export type RoundCell = {
  memberIdx: number;
  roundIdx: number;
  label: string;
};

// Render order of the interleaved table. A member with fewer sets than the
// current round contributes no cell, so asymmetric groups stay safe.
export function interleaveRounds(counts: ReadonlyArray<number>): RoundCell[] {
  const rounds = groupMaxRounds(counts);
  const cells: RoundCell[] = [];
  for (let r = 0; r < rounds; r++) {
    for (let m = 0; m < counts.length; m++) {
      if (r < (counts[m] ?? 0)) {
        cells.push({
          memberIdx: m,
          roundIdx: r,
          label: roundLabel(r, m, counts.length),
        });
      }
    }
  }
  return cells;
}

// Round removal for ONE member, with the logger's safety rules: best-effort,
// never a forced trim. A member with fewer sets than `roundIdx` is left
// untouched, and the last remaining set is always preserved (every member
// keeps ≥1). Returns the input array (same reference) on a no-op so callers
// can keep identity checks.
export function removeRoundFromSets<S>(sets: S[], roundIdx: number): S[] {
  if (sets.length <= 1) return sets;
  if (roundIdx < 0 || roundIdx >= sets.length) return sets;
  return sets.filter((_, i) => i !== roundIdx);
}
